import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import '../styles/Dashboard.css';
import UserSettings from './UserSettings';
import MiCARegisterNew from './MiCARegisterNew';
import LiveDataFeed from './LiveDataFeed';
import ComplianceOperator from './ComplianceOperator';
import Vault from './Vault';
import Ledger from './Ledger';
import EUFrameworksLanding from '../components/EUFrameworksLanding';
import RegulatoryTaxonomy from './RegulatoryTaxonomy';
import NordicBalticNCAs from './NordicBalticNCAs';
import DocumentLibrary from './DocumentLibrary';
import Authorities from './Authorities';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import KYBVerification from './KYBVerification';
import EKYBVerification from './EKYBVerification';
import { useNews } from '../hooks';
import { useAzureAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../constants';

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAzureAuth();
  const { news, loadingNews, hasMoreNews, latestNewsDate, loadMoreNews } = useNews();
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedJurisdictions, setSelectedJurisdictions] = useState(['FI']);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(65);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('ALL');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/search?query=${encodeURIComponent(searchQuery)}&filter=${selectedFilter}`
      );
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      } else {
        console.error('Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!value.trim() || value.trim().length < 3) {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    if (searchQuery.trim() && searchQuery.trim().length >= 3) {
      // Re-run search with new filter
      setTimeout(() => handleSearch(), 0);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() && searchQuery.trim().length >= 3) {
      const timeoutId = setTimeout(() => {
        handleSearch();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);

  const toggleJurisdiction = (code) => {
    if (selectedJurisdictions.includes(code)) {
      if (selectedJurisdictions.length > 1) {
        setSelectedJurisdictions(selectedJurisdictions.filter(j => j !== code));
      }
    } else {
      setSelectedJurisdictions([...selectedJurisdictions, code]);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    if (step === 2) {
      simulateAnalysis();
    }
  };

  const simulateAnalysis = () => {
    let progress = 65;
    const interval = setInterval(() => {
      progress += 5;
      setAnalysisProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        setTimeout(() => goToStep(3), 1000);
      }
    }, 300);
  };

  const handleFileUpload = (file) => {
    if (file) {
      setUploadedFile(file);
      setTimeout(() => goToStep(2), 500);
    }
  };

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard/live-intelligence" replace />} />
      <Route path="/regulatory-intelligence" element={<DashboardMain currentUser={currentUser} showUserMenu={showUserMenu} setShowUserMenu={setShowUserMenu} logout={logout} currentStep={currentStep} goToStep={goToStep} selectedJurisdictions={selectedJurisdictions} toggleJurisdiction={toggleJurisdiction} handleFileUpload={handleFileUpload} analysisProgress={analysisProgress} navigate={navigate} news={news} loadingNews={loadingNews} hasMoreNews={hasMoreNews} loadMoreNews={loadMoreNews} latestNewsDate={latestNewsDate} searchQuery={searchQuery} handleSearchInput={handleSearchInput} selectedFilter={selectedFilter} handleFilterChange={handleFilterChange} isSearching={isSearching} searchResults={searchResults} />} />
      <Route path="/settings/*" element={<UserSettings currentUser={currentUser} onUpdate={(user) => {}} />} />
      <Route path="/kyb" element={<KYBVerification currentUser={currentUser} />} />
      <Route path="/ekyb" element={<KYBVerification currentUser={currentUser} />} />
      <Route path="/ekyb-verification" element={<EKYBVerification />} />
      <Route path="/mica" element={<MiCARegisterNew currentUser={currentUser} />} />
      <Route path="/live-feed" element={<LiveDataFeed />} />
      <Route path="/live-intelligence" element={<LiveDataFeed />} />
      <Route path="/compliance-operator" element={<ComplianceOperator />} />
      <Route path="/vault" element={<Vault />} />
      <Route path="/ledger" element={<Ledger />} />
      <Route path="/regulatory-heat-map" element={<EUFrameworksLanding />} />
      <Route path="/regulatory-coverage" element={<RegulatoryTaxonomy />} />
      <Route path="/nordic-baltic" element={<NordicBalticNCAs />} />
      <Route path="/document-library" element={<DocumentLibrary />} />
      <Route path="/authorities" element={<Authorities />} />
    </Routes>
  );
};

const DashboardMain = ({ currentUser, showUserMenu, setShowUserMenu, logout, currentStep, goToStep, selectedJurisdictions, toggleJurisdiction, handleFileUpload, analysisProgress, navigate, news, loadingNews, hasMoreNews, loadMoreNews, latestNewsDate, searchQuery, handleSearchInput, selectedFilter, handleFilterChange, isSearching, searchResults }) => {
  const jurisdictions = [
    { code: 'FI', name: 'Finland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'DK', name: 'Denmark' },
    { code: 'EE', name: 'Estonia' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LT', name: 'Lithuania' }
  ];

  return (
    <>
      <Header 
        currentUser={currentUser} 
        showUserMenu={showUserMenu} 
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />

      <div className="app">
        <Sidebar />

        <div className="main-content">

        {/* Content Area */}
        <div className="content-area">
          {/* Info Banner - Page Style */}
          <div className="page-info-banner">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <div>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#8a8886', letterSpacing: '0.5px', marginBottom: '8px'}}>REGULATORY INTELLIGENCE</div>
                <div className="page-info-title">Regulatory Intelligence</div>
                <div className="page-info-text">
                  Real-time regulatory updates and intelligence across EU financial services. Track EUR-Lex, ESMA, EBA, and ECB announcements with automated monitoring.
                </div>
              </div>
              <div style={{fontSize: '13px', color: '#605e5c', whiteSpace: 'nowrap', marginLeft: '24px'}}>
                Last updated: {latestNewsDate ? new Date(latestNewsDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '10:00 AM'}
              </div>
            </div>
          </div>

          {/* Compact Data Sources Grid */}
          <div className="sources-grid">
            {/* EUR-Lex */}
            <div className="source-card" style={{animationDelay: '0s'}}>
              <div className="source-card-header">
                <div className="source-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#605e5c" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                </div>
                <div className="source-card-title-row">
                  <h3 className="source-name">EUR-Lex</h3>
                  <span className="status-badge">ACTIVE</span>
                </div>
                <div className="source-description">
                  Official EU legal database with real-time access to regulations and directives
                </div>
              </div>
              <div className="source-card-body">
                <div className="source-items">
                  <div className="source-item">Official Journal (XML/JSON)</div>
                  <div className="source-item">SPARQL endpoint</div>
                  <div className="source-item">Daily updates</div>
                </div>
              </div>
              <div className="source-card-footer">
                <a href="https://eur-lex.europa.eu" target="_blank" rel="noopener noreferrer" className="source-link">
                  View Source →
                </a>
              </div>
            </div>

            {/* ESMA Registry */}
            <div className="source-card" style={{animationDelay: '0.1s'}}>
              <div className="source-card-header">
                <div className="source-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#605e5c" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <line x1="9" y1="9" x2="15" y2="9"/>
                    <line x1="9" y1="15" x2="15" y2="15"/>
                  </svg>
                </div>
                <div className="source-card-title-row">
                  <h3 className="source-name">ESMA Registry</h3>
                  <span className="status-badge">ACTIVE</span>
                </div>
                <div className="source-description">
                  Securities & Markets Authority with comprehensive regulatory standards
                </div>
              </div>
              <div className="source-card-body">
                <div className="source-items">
                  <div className="source-item">Interactive Single Rulebook</div>
                  <div className="source-item">Press releases</div>
                  <div className="source-item">Technical Standards</div>
                </div>
              </div>
              <div className="source-card-footer">
                <a href="https://www.esma.europa.eu" target="_blank" rel="noopener noreferrer" className="source-link">
                  View Source →
                </a>
              </div>
            </div>

            {/* EBA Rulebook */}
            <div className="source-card" style={{animationDelay: '0.2s'}}>
              <div className="source-card-header">
                <div className="source-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#605e5c" strokeWidth="2">
                    <path d="M12 2l9 4.9V12c0 5.5-3.8 10.7-9 12-5.2-1.3-9-6.5-9-12V6.9L12 2z"/>
                  </svg>
                </div>
                <div className="source-card-title-row">
                  <h3 className="source-name">EBA Rulebook</h3>
                  <span className="status-badge">ACTIVE</span>
                </div>
                <div className="source-description">
                  Banking Authority with guidelines and regulatory frameworks
                </div>
              </div>
              <div className="source-card-body">
                <div className="source-items">
                  <div className="source-item">Press releases</div>
                  <div className="source-item">Work Programme 2026</div>
                  <div className="source-item">DORA/MiCA updates</div>
                </div>
              </div>
              <div className="source-card-footer">
                <a href="https://www.eba.europa.eu" target="_blank" rel="noopener noreferrer" className="source-link">
                  View Source →
                </a>
              </div>
            </div>

            {/* ECB Database */}
            <div className="source-card" style={{animationDelay: '0.3s'}}>
              <div className="source-card-header">
                <div className="source-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#605e5c" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                </div>
                <div className="source-card-title-row">
                  <h3 className="source-name">ECB Database</h3>
                  <span className="status-badge">ACTIVE</span>
                </div>
                <div className="source-description">
                  European Central Bank with banking supervision updates
                </div>
              </div>
              <div className="source-card-body">
                <div className="source-items">
                  <div className="source-item">Banking Supervision</div>
                  <div className="source-item">Press releases RSS</div>
                  <div className="source-item">Speeches & Interviews</div>
                </div>
              </div>
              <div className="source-card-footer">
                <a href="https://www.bankingsupervision.europa.eu" target="_blank" rel="noopener noreferrer" className="source-link">
                  View Source →
                </a>
              </div>
            </div>
          </div>

          {/* Search Section */}
          <div className="search-section" style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            marginTop: '32px',
            marginBottom: '24px',
            padding: '0'
          }}>
            <div className="search-wrapper" style={{
              position: 'relative',
              flex: 1
            }}>
              <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#605e5c" strokeWidth="2" style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }}>
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                className="search-input"
                value={searchQuery}
                onChange={handleSearchInput}
                placeholder="Search regulations, press releases, technical standards..."
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 48px',
                  border: '1.5px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-primary)',
                  fontWeight: '400',
                  color: '#1F2937',
                  background: '#FFFFFF',
                  outline: 'none',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0052CC';
                  e.target.style.boxShadow = '0 0 0 4px rgba(0, 82, 204, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 0, 0, 0.12)';
                  e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.04)';
                }}
              />
            </div>
            {/* Filters hidden - cleaner design */}
          </div>

          {/* Recent Updates / Search Results */}
          <section className="section">
          <div className="section-header">
            <h2 className="section-title">{isSearching ? `I find: (${searchResults.length})` : 'Recent Updates'}</h2>
          </div>

          {loadingNews && (isSearching ? searchResults.length === 0 : news.length === 0) ? (
            <div style={{padding: '20px', textAlign: 'center', color: 'var(--text-secondary)'}}>
              {isSearching ? 'Searching...' : 'Loading news...'}
            </div>
          ) : (isSearching ? searchResults.length === 0 : news.length === 0) ? (
            <div style={{padding: '20px', textAlign: 'center', color: 'var(--text-secondary)'}}>
              {isSearching ? 'No results found' : 'No news available'}
            </div>
          ) : (
            <>
              {(isSearching ? searchResults : news).map((item) => {
                // Handle both pubdate (lowercase) and pubDate (camelCase) formats
                const dateValue = item.pubdate || item.pubDate || item.last_pubdate;
                const pubDate = new Date(dateValue);
                const formattedDate = pubDate.toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric' 
                });
                
                return (
                  <div key={item.id} className="notification">
                    <div className="notification-header">
                      <div className="notification-title">{item.title}</div>
                      <div className="notification-time">{formattedDate}</div>
                    </div>
                    <div className="notification-body">
                      {item.description ? (
                        item.description
                      ) : item.source === 'esma_library' ? (
                        <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                          {item.reference && (
                            <div style={{ marginBottom: '4px' }}>
                              <strong>Reference:</strong> {item.reference}
                            </div>
                          )}
                          {item.section && (
                            <div style={{ marginBottom: '4px' }}>
                              <strong>Section:</strong> {item.section}
                            </div>
                          )}
                          {item.type && (
                            <div>
                              <strong>Type:</strong> {item.type}
                            </div>
                          )}
                        </div>
                      ) : (
                        'No description available'
                      )}
                    </div>
                    {item.link && (
                      <div style={{marginTop: '8px'}}>
                        <a 
                          href={item.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{
                            fontSize: '13px',
                            color: 'var(--primary)',
                            textDecoration: 'none',
                            fontWeight: '500'
                          }}
                        >
                          more info →
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {!isSearching && hasMoreNews && (
                <div style={{marginTop: '16px', textAlign: 'center'}}>
                  <button 
                    onClick={loadMoreNews}
                    disabled={loadingNews}
                    style={{
                      padding: '10px 20px',
                      background: 'var(--surface)',
                      border: '1px solid var(--divider)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: loadingNews ? 'not-allowed' : 'pointer',
                      opacity: loadingNews ? 0.6 : 1
                    }}
                  >
                    {loadingNews ? 'Loading...' : 'Show more news'}
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        </div>
      </div>
    </div>
    </>
  );
};

// Step Components
const StepUpload = ({ jurisdictions, selectedJurisdictions, toggleJurisdiction, handleFileUpload }) => (
  <div className="step-content active">
    <h3 style={{fontSize: '16px', fontWeight: 600, marginBottom: '16px', color: 'var(--text-primary)'}}>
      Select Jurisdictions
    </h3>

    <div className="jurisdiction-grid">
      {jurisdictions.map(j => (
        <div
          key={j.code}
          className={`jurisdiction-item ${selectedJurisdictions.includes(j.code) ? 'selected' : ''}`}
          onClick={() => toggleJurisdiction(j.code)}
        >
          <div className="jurisdiction-code">{j.code}</div>
          <div className="jurisdiction-name">{j.name}</div>
        </div>
      ))}
    </div>

    <div className="upload-zone" onClick={() => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.docx,.txt';
      input.onchange = (e) => handleFileUpload(e.target.files[0]);
      input.click();
    }}>
      <div className="upload-text">Upload product specification</div>
      <div className="upload-hint">PDF, DOCX, TXT · Drop file or click to browse</div>
    </div>

    <div style={{display: 'flex', gap: '8px', justifyContent: 'center'}}>
      <button className="btn btn-secondary" onClick={() => handleFileUpload({name: 'stablecoin.pdf'})}>Stablecoin Template</button>
      <button className="btn btn-secondary" onClick={() => handleFileUpload({name: 'casp.pdf'})}>CASP Template</button>
      <button className="btn btn-secondary" onClick={() => handleFileUpload({name: 'custodian.pdf'})}>Custodian Template</button>
    </div>
  </div>
);

const StepAnalysis = ({ analysisProgress, goToStep }) => (
  <div className="step-content active">
    <h3 style={{fontSize: '16px', fontWeight: 600, marginBottom: '24px', color: 'var(--text-primary)'}}>
      Analyzing compliance requirements
    </h3>

    <div className="analysis-panel">
      <div className="analysis-row completed">
        <div className="analysis-icon completed">✓</div>
        <div className="analysis-label">Extracting product details</div>
        <div className="analysis-value">Stablecoin Service</div>
      </div>
      
      <div className="analysis-row completed">
        <div className="analysis-icon completed">✓</div>
        <div className="analysis-label">Loading MiCA requirements</div>
        <div className="analysis-value">Title III - ARTs</div>
      </div>
      
      <div className="analysis-row active">
        <div className="analysis-icon active">◐</div>
        <div className="analysis-label">Checking Finnish regulations</div>
        <div className="analysis-value">Finanssivalvonta</div>
      </div>
      
      <div className="analysis-row">
        <div className="analysis-icon">·</div>
        <div className="analysis-label">Cross-referencing requirements</div>
        <div className="analysis-value">Pending</div>
      </div>
    </div>

    <div className="progress-bar">
      <div className="progress-fill" style={{width: `${analysisProgress}%`}}></div>
    </div>
    <div className="progress-label">{analysisProgress}%</div>
  </div>
);

const StepRequirements = ({ goToStep }) => (
  <div className="step-content active">
    <h3 style={{fontSize: '16px', fontWeight: 600, marginBottom: '24px', color: 'var(--text-primary)'}}>
      Compliance Requirements Identified
    </h3>

    <table className="requirements-table">
      <thead>
        <tr>
          <th>Requirement</th>
          <th>Regulation</th>
          <th>Status</th>
          <th>Priority</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Asset-Referenced Token Authorization</td>
          <td>MiCA Title III</td>
          <td><span className="requirement-status status-pass">✓ Met</span></td>
          <td>Critical</td>
        </tr>
        <tr>
          <td>Reserve Management (30% liquidity)</td>
          <td>MiCA Art. 36</td>
          <td><span className="requirement-status status-fail">✗ Missing</span></td>
          <td>Critical</td>
        </tr>
        <tr>
          <td>Redemption Mechanism</td>
          <td>MiCA Art. 42</td>
          <td><span className="requirement-status status-pass">✓ Met</span></td>
          <td>High</td>
        </tr>
        <tr>
          <td>Qualified Custody</td>
          <td>MiCA Art. 37</td>
          <td><span className="requirement-status status-missing">! Partial</span></td>
          <td>High</td>
        </tr>
        <tr>
          <td>White Paper Publication</td>
          <td>MiCA Art. 19</td>
          <td><span className="requirement-status status-pass">✓ Met</span></td>
          <td>Medium</td>
        </tr>
        <tr>
          <td>Quarterly Reporting (Finland)</td>
          <td>FIN-FSA Rules</td>
          <td><span className="requirement-status status-fail">✗ Missing</span></td>
          <td>High</td>
        </tr>
      </tbody>
    </table>

    <div style={{textAlign: 'center', marginTop: '32px'}}>
      <button className="btn btn-primary" onClick={() => goToStep(4)}>Proceed to Audit</button>
    </div>
  </div>
);

const StepAudit = ({ goToStep }) => (
  <div className="step-content active">
    <h3 style={{fontSize: '16px', fontWeight: 600, marginBottom: '24px', color: 'var(--text-primary)'}}>
      Running Compliance Audit
    </h3>

    <div style={{background: 'var(--background)', padding: '24px', borderRadius: '2px', marginBottom: '24px'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
        <span style={{fontSize: '13px', color: 'var(--text-secondary)'}}>Progress</span>
        <span style={{fontSize: '20px', fontWeight: 600, color: 'var(--primary)'}}>78%</span>
      </div>
      <div className="progress-bar">
        <div className="progress-fill" style={{width: '78%'}}></div>
      </div>
    </div>

    <div className="module-list">
      <div className="module-item">
        <div>
          <div className="module-label">AML/KYC Module</div>
          <div className="module-desc">Anti-Money Laundering compliance</div>
        </div>
        <span className="module-status status-deployed">Deployed</span>
      </div>
      
      <div className="module-item">
        <div>
          <div className="module-label">Reserve Management Module</div>
          <div className="module-desc">30% liquidity requirement tracking</div>
        </div>
        <span className="module-status status-required">Required</span>
      </div>
      
      <div className="module-item">
        <div>
          <div className="module-label">Custody Module</div>
          <div className="module-desc">Qualified custodian integration</div>
        </div>
        <span className="module-status status-deployed">Deployed</span>
      </div>
      
      <div className="module-item">
        <div>
          <div className="module-label">Reporting Module (FIN)</div>
          <div className="module-desc">Finanssivalvonta quarterly reports</div>
        </div>
        <span className="module-status status-required">Required</span>
      </div>
    </div>

    <div style={{textAlign: 'center', marginTop: '32px'}}>
      <button className="btn btn-primary" onClick={() => goToStep(5)}>View Certificate</button>
    </div>
  </div>
);

const StepCertificate = ({ goToStep }) => (
  <div className="step-content active">
    <div className="certificate">
      <div className="certificate-watermark">TRANSTAR</div>
      
      <div className="certificate-content">
        <div className="certificate-header">
          <div className="certificate-badge">TRANSTAR</div>
          <div className="certificate-subtitle">Regulatory Automation Platform</div>
        </div>

        <div className="certificate-title">Compliance Certificate</div>
        
        <div className="certificate-text">
          This certifies that the specified product has been analyzed against applicable 
          regulatory requirements for the selected jurisdictions. The compliance assessment 
          was performed using Transtar's automated regulatory intelligence system.
        </div>

        <div className="certificate-details">
          <div className="certificate-detail">
            <div className="detail-label">Product Type</div>
            <div className="detail-value">Asset-Referenced Token</div>
          </div>
          <div className="certificate-detail">
            <div className="detail-label">Primary Regulation</div>
            <div className="detail-value">MiCA Title III</div>
          </div>
          <div className="certificate-detail">
            <div className="detail-label">Jurisdictions</div>
            <div className="detail-value">Finland, Sweden, Denmark</div>
          </div>
          <div className="certificate-detail">
            <div className="detail-label">Assessment Date</div>
            <div className="detail-value">January 15, 2026</div>
          </div>
          <div className="certificate-detail">
            <div className="detail-label">Compliance Score</div>
            <div className="detail-value" style={{color: 'var(--success)'}}>78%</div>
          </div>
          <div className="certificate-detail">
            <div className="detail-label">Certificate ID</div>
            <div className="detail-value">TSR-2026-001547</div>
          </div>
        </div>

        <div className="certificate-footer">
          <div className="signature">
            <div className="signature-line"></div>
            <div className="signature-name">Automated Assessment</div>
            <div className="signature-title">Transtar Compliance Engine</div>
          </div>
          <div className="signature">
            <div className="signature-line"></div>
            <div className="signature-name">Hedera Consensus</div>
            <div className="signature-title">Blockchain Verification</div>
          </div>
        </div>
      </div>
    </div>

    <div style={{display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '24px'}}>
      <button className="btn btn-secondary">Download PDF</button>
      <button className="btn btn-secondary">Download Report</button>
      <button className="btn btn-primary" onClick={() => goToStep(1)}>New Test</button>
    </div>
  </div>
);

export default Dashboard;
